import { WritableSignal } from '@angular/core';

const LOCK_THRESHOLD_PX = 10;
const SWIPE_WIDTH_RATIO = 0.18;
const MIN_SWIPE_PX = 48;

export type H1SliderSwipeHandlers = {
  onPointerDown: (event: PointerEvent) => void;
  onPointerMove: (event: PointerEvent) => void;
  onPointerUp: (event: PointerEvent) => void;
  onPointerCancel: (event: PointerEvent) => void;
};

export function sliderTrackTransform(activeSlide: number, dragPx: number): string {
  return `translateX(calc(${-activeSlide * 100}% + ${dragPx}px))`;
}

/** Pointer/touch swipe for full-width `.h1-slider` carousels (mouse drag + mobile swipe). */
export function createH1SliderSwipeHandlers(options: {
  activeSlide: WritableSignal<number>;
  slideCount: () => number;
  viewportWidth: () => number;
  dragOffsetPx: WritableSignal<number>;
  isDragging: WritableSignal<boolean>;
}): H1SliderSwipeHandlers {
  let startX = 0;
  let startY = 0;
  let pointerId: number | null = null;
  let axis: 'none' | 'x' | 'y' = 'none';
  let captureEl: HTMLElement | null = null;

  const releaseCapture = (): void => {
    if (captureEl && pointerId != null) {
      try {
        captureEl.releasePointerCapture(pointerId);
      } catch {
        /* already released */
      }
    }
    pointerId = null;
    axis = 'none';
    captureEl = null;
  };

  const finishSwipe = (): void => {
    const offset = options.dragOffsetPx();
    const width = options.viewportWidth() || 1;
    const threshold = Math.max(MIN_SWIPE_PX, width * SWIPE_WIDTH_RATIO);
    const idx = options.activeSlide();
    const last = options.slideCount() - 1;

    if (offset < -threshold) {
      options.activeSlide.set(Math.min(last, idx + 1));
    } else if (offset > threshold) {
      options.activeSlide.set(Math.max(0, idx - 1));
    }

    options.dragOffsetPx.set(0);
    options.isDragging.set(false);
    releaseCapture();
  };

  return {
    onPointerDown: (event: PointerEvent) => {
      if (event.button !== 0) return;
      if (options.slideCount() <= 1) return;
      const target = event.target as HTMLElement;
      if (target.closest('button, a')) return;

      startX = event.clientX;
      startY = event.clientY;
      pointerId = event.pointerId;
      axis = 'none';
      captureEl = event.currentTarget as HTMLElement;
      captureEl.setPointerCapture(event.pointerId);
    },

    onPointerMove: (event: PointerEvent) => {
      if (event.pointerId !== pointerId || !captureEl) return;

      const dx = event.clientX - startX;
      const dy = event.clientY - startY;

      if (axis === 'none') {
        if (Math.hypot(dx, dy) < LOCK_THRESHOLD_PX) return;
        axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
        if (axis === 'y') {
          releaseCapture();
          return;
        }
        options.isDragging.set(true);
      }
      if (axis !== 'x') return;

      event.preventDefault();

      const idx = options.activeSlide();
      const last = options.slideCount() - 1;
      let offset = dx;
      if (idx === 0 && offset > 0) offset *= 0.3;
      else if (idx === last && offset < 0) offset *= 0.3;
      options.dragOffsetPx.set(offset);
    },

    onPointerUp: (event: PointerEvent) => {
      if (event.pointerId !== pointerId) return;
      finishSwipe();
    },

    onPointerCancel: (event: PointerEvent) => {
      if (event.pointerId !== pointerId) return;
      finishSwipe();
    },
  };
}
